import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import {
	ActionIcon,
	Anchor,
	Box,
	Breadcrumbs,
	Container,
	Group,
	LoadingOverlay,
	SegmentedControl,
	Text,
} from '@mantine/core';
import { useInvoices } from '../../hooks/payments.hooks';
import { useModals } from '@mantine/modals';
import { useSimpleMutation } from '../../queryUtils';
import { useApi } from '../../hooks/api.hooks';
import NoPrint from '../../components/NoPrint';
import { useCompanyInfo } from '../../hooks/companyInfo.hooks';
import localeMessages from '../../localeMessages';
import PaymentDetails from '../../layouts/payments/PaymentDetails';
import { Trash } from 'tabler-icons-react';

export default function InvoiceDetails(): JSX.Element {
	const api = useApi();
	const modals = useModals();
	const routeParams = useParams();
	const navigate = useNavigate();
	const [language, setLanguage] = useState('bs');
	const deleteInvoice = useSimpleMutation(api.finance.invoices.deleteInvoice);
	const {
		data: invoices,
		status: invoicesStatus,
		isError: isInvoicesError,
	} = useInvoices();
	const {
		data: companyInfo,
		status: companyInfoStatus,
		isError: isCompanyInfoError,
	} = useCompanyInfo();
	const invoice = invoices?.find((item) => item.id === routeParams.invoiceId);

	const goToInvoices = () => navigate('..');

	if (isInvoicesError || isCompanyInfoError) {
		goToInvoices();
	}

	if (invoices?.length && !invoice) {
		goToInvoices();
	}

	if (invoicesStatus !== 'success' || companyInfoStatus !== 'success' || !invoice || !companyInfo) {
		return <LoadingOverlay visible />;
	}

	const openDeleteModal = () => modals.openConfirmModal({
		title: 'Remove Client',
		children: (
			<Text>{`Are you sure you want to remove invoice ${invoice.invoiceNumber}/${invoice.fiscalYear}?`}</Text>
		),
		labels: { confirm: 'Delete', cancel: 'Cancel' },
		confirmProps: {
			color: 'red',
			uppercase: true,
		},
		cancelProps: {
			color: 'gray',
			variant: 'subtle',
			uppercase: true,
		},
		onConfirm: () => {
			deleteInvoice({ invoiceId: invoice.id });
			goToInvoices();
		},
	});

	return <Container size={650} sx={{ height: '100%' }}>
		<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
			<NoPrint>
				<Group direction="column" grow>
					<Group position="apart">
						<Breadcrumbs>
							<Anchor component={Link} to="..">Invoices</Anchor>
							<Anchor component={Link} to=".">Invoice</Anchor>
						</Breadcrumbs>
						<SegmentedControl
							value={language}
							onChange={setLanguage}
							data={[
								{
									label: 'English',
									value: 'en',
								},
								{
									label: 'Bosanski',
									value: 'bs',
								},
							]}
							color="blue"
							size="xs"
						/>
					</Group>
				</Group>
			</NoPrint>
			<IntlProvider
				locale={language}
				messages={localeMessages[language]}
			>
				<PaymentDetails
					type="invoice"
					value={invoice}
					companyInfo={companyInfo}
				/>
			</IntlProvider>
			<NoPrint>
				<Group position="right" sx={{ width: '100%' }}>
					<ActionIcon color="red" onClick={openDeleteModal} variant="outline" size="lg">
						<Trash size={14} />
					</ActionIcon>
				</Group>
			</NoPrint>
		</Box>
	</Container>;
}
